import React, { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { LocationPros } from "../../../../types/baseTypes";
import { fetchLocationsByService } from "../../../../api/locations/locationsClient";

type KeyHandoverContextState = {
  /**
   * While loading: undefined
   * No locations: null or [] (depending on API)
   * Success: LocationPros[]
   */
  locations: LocationPros[] | null | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown | null;
};

const KeyHandoverContext = createContext<KeyHandoverContextState | undefined>(
  undefined
);

export function KeyHandoverProvider({
  children,
  serviceFilter = "keys",
}: {
  children: React.ReactNode;
  /**
   * Filter locations by service (e.g., "keys", "rental").
   */
  serviceFilter?: string;
}) {
  const { data, isLoading, isError, error } = useQuery<LocationPros[]>({
    queryKey: ["locations", serviceFilter],
    queryFn: () => fetchLocationsByService(serviceFilter),
    // keep semantics similar to previous behavior for loading
    staleTime: 1000 * 60, // 1 minute
  });

  const value = useMemo<KeyHandoverContextState>(
    () => ({ locations: isLoading ? undefined : data ?? null, isLoading, isError, error: error ?? null }),
    [data, error, isError, isLoading]
  );
  return <KeyHandoverContext.Provider value={value}>{children}</KeyHandoverContext.Provider>;
}

export function useKeyHandoverLocations() {
  const ctx = useContext(KeyHandoverContext);
  if (!ctx) {
    throw new Error(
      "useKeyHandoverLocations must be used within a KeyHandoverProvider"
    );
  }
  return ctx.locations;
}

export function useKeyHandoverStatus() {
  const ctx = useContext(KeyHandoverContext);
  if (!ctx) {
    throw new Error(
      "useKeyHandoverStatus must be used within a KeyHandoverProvider"
    );
  }
  return { isLoading: ctx.isLoading, isError: ctx.isError, error: ctx.error };
}

// Alias with more explicit name for discoverability; keep original for back-compat
export function useKeyHandoverQueryStatus() {
  return useKeyHandoverStatus();
}

/**
 * Convenience hook returning both data and status.
 */
export function useKeyHandover() {
  const ctx = useContext(KeyHandoverContext);
  if (!ctx) {
    throw new Error("useKeyHandover must be used within a KeyHandoverProvider");
  }
  const { locations, isLoading, isError, error } = ctx;
  return { locations, isLoading, isError, error };
}
